import { JsonTree } from '../../JsonTree';
import { Box, Card, Flex, Grid, Table, Title } from '@mantine/core';
import { IconFileText } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import PageSizeOptions from '../../../constants/pageSizeOptions';
import { daysAgoISODate, dateToEndOfDay, dateToStartOfDay, toISODate, todayISODate } from '../../../helpers/dateUtils';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import MetricsSelector from '../../MetricsSelector';
import Pager from '../../Pager/Pager';
import SearchBar from '../../SearchBar';
import Spinner from '../../Spinner';

const fetchDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, search) =>
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/auditLog${search || ''}`),
  500
);

const initialPeriodDays = 7;

const SettingsTabAuditLog = ({ domain }) => {
  const [logEntries, setLogEntries] = useState();
  const [loadError, setLoadError] = useState();
  const [totalCount, setTotalCount] = useState();
  const [pageCount, setPageCount] = useState();
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();

  const parsedSearch = queryString.parse(location.search);

  // Store ISO date strings (YYYY-MM-DD) for the picker
  const [startDate, setStartDate] = useState(
    parsedSearch.startTime ? toISODate(parsedSearch.startTime) : daysAgoISODate(initialPeriodDays - 1)
  );
  const [endDate, setEndDate] = useState(parsedSearch.endTime ? toISODate(parsedSearch.endTime) : todayISODate());
  const [from, setFrom] = useState(parsedSearch.from || 0);
  const [size, setSize] = useState(parsedSearch.size || PageSizeOptions[0]);
  const [searchValue, setSearchValue] = useState(parsedSearch.search || '');

  useEffect(() => {
    if (queryString.parse(location.search).tab !== 'auditLog') {
      return;
    }
    // Convert ISO date strings to full timestamps for the API
    const startTime = dateToStartOfDay(startDate, 'local');
    const endTime = dateToEndOfDay(endDate, 'local');

    const params = { tab: 'auditLog', startTime, endTime, from, size };
    if (searchValue) {
      params.search = searchValue;
    }
    navigate({
      search: queryString.stringify(params, { arrayFormat: 'bracket' }),
    });
  }, [startDate, endDate, from, size, searchValue, navigate, location.search]);

  const setPageSize = async (pageSize) => {
    setSize(pageSize);
  };

  const handlePageClick = (e) => {
    const selectedPage = e.selected;
    const newOffset = (selectedPage - 1) * size;
    if (newOffset !== from) {
      setFrom(newOffset);
    }
  };

  useEffect(() => {
    setLogEntries(undefined);
    fetchDebounced(fetch, domain.accountId, domain.id, location.search)
      .then(({ entries, totalCount }) => {
        setLogEntries(entries);
        setTotalCount(totalCount);
        setPageCount(Math.ceil(totalCount / size));
      })
      .catch(setLoadError);
  }, [domain.accountId, domain.id, fetch, size, location.search]);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  const renderContent = () => {
    if (logEntries === undefined) {
      return <Spinner />;
    }

    if (logEntries.length === 0) {
      return (
        <Card.Section p="md" pb="xl">
          <Title order={3} pb="xl">
            No entries found
          </Title>
        </Card.Section>
      );
    }

    return (
      <>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Time</Table.Th>
              <Table.Th>User</Table.Th>
              <Table.Th>Entity</Table.Th>
              <Table.Th>Entity Id</Table.Th>
              <Table.Th>Action</Table.Th>
              <Table.Th>Request Body</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {logEntries.map((entry, i) => (
              <Table.Tr key={i}>
                <Table.Td>{new Date(entry.timestamp).toLocaleString()}</Table.Td>
                <Table.Td>
                  {entry.user.firstName} {entry.user.lastName} &lt;{entry.user.email}&gt;
                </Table.Td>
                <Table.Td>{entry.entity.type}</Table.Td>
                <Table.Td>{entry.entity.id}</Table.Td>
                <Table.Td>{entry.action}</Table.Td>
                <Table.Td>
                  <JsonTree data={entry.body} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Grid p="md">
          <Grid.Col span={12}>
            <Pager
              pageSize={parseInt(size)}
              pageCount={pageCount}
              onPageChange={handlePageClick}
              initialPage={Math.round(parseInt(from || '0') / parseInt(size) + 1)}
              setPageSize={setPageSize}
            />
          </Grid.Col>
        </Grid>
      </>
    );
  };

  return (
    <>
      <Card withBorder>
        <Card.Section p="md">
          <Flex justify="space-between" align="center" wrap="wrap" gap="md">
            <Title order={4}>
              {logEntries && logEntries.length > 0 && (
                <>
                  <IconFileText size={20} style={{ marginRight: 8, display: 'inline' }} /> {totalCount} Log Entries
                </>
              )}
            </Title>
            <Flex align="center" gap="md" wrap="wrap">
              <Box style={{ minWidth: 200 }}>
                <SearchBar
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    setFrom(0);
                  }}
                />
              </Box>
              <MetricsSelector
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                rowSize="12"
              />
            </Flex>
          </Flex>
        </Card.Section>
        {renderContent()}
      </Card>
    </>
  );
};

export default SettingsTabAuditLog;
